/**
 * Pure translation overlay engine — no React, no side effects.
 * Clones and patches a content schema object using asset bundle entries.
 *
 * Key format:  {sourceContentId}|schemaData.{jsonPath}
 * Example:     "130|schemaData.sections[2].content"
 */

export const parseEntryKey = (key) => {
  if (typeof key !== 'string') return null;
  const pipe = key.indexOf('|');
  if (pipe <= 0) return null;
  return { sourceContentId: key.substring(0, pipe), jsonPath: key.substring(pipe + 1) };
};

export const tokenisePath = (path) => {
  const tokens = [];
  for (const part of path.split('.')) {
    const bracketIdx = part.indexOf('[');
    if (bracketIdx === -1) {
      tokens.push(part);
    } else {
      if (bracketIdx > 0) tokens.push(part.substring(0, bracketIdx));
      const matches = part.substring(bracketIdx).match(/\[(\d+)\]/g) || [];
      for (const m of matches) tokens.push(parseInt(m.slice(1, -1), 10));
    }
  }
  return tokens;
};

export const setValueAtPath = (obj, path, value) => {
  const tokens = tokenisePath(path);
  let cur = obj;
  for (let i = 0; i < tokens.length - 1; i++) {
    if (cur == null || typeof cur !== 'object') return;
    cur = cur[tokens[i]];
  }
  if (cur != null && typeof cur === 'object') {
    cur[tokens[tokens.length - 1]] = value;
  }
};

/**
 * Apply translations to a content schema.
 * @param {object} contentSchema  - parsed SchemaData: { title, sections }
 * @param {string|number} sourceContentId - DB Content.Id
 * @param {object|null} bundle - loaded asset bundle (null = no-op)
 * @returns {object} deep-cloned translated copy (original is never mutated)
 */
export const applyTranslation = (contentSchema, sourceContentId, bundle, patientValues = null) => {
  if (!bundle || !bundle.entries || !contentSchema || sourceContentId == null) {
    return contentSchema;
  }
  const sid = String(sourceContentId);
  const wrapped = { schemaData: JSON.parse(JSON.stringify(contentSchema)) };
  let applied = 0;

  for (const [key, value] of Object.entries(bundle.entries)) {
    if (!value) continue;
    const parsed = parseEntryKey(key);
    if (!parsed || parsed.sourceContentId !== sid) continue;
    setValueAtPath(wrapped, parsed.jsonPath, value);
    applied++;
  }

  if (process.env.NODE_ENV === 'development' && applied > 0) {
    console.log(`[Translation] Content ${sid}: ${applied} fields applied`);
  }

  // Resolve any {{token}} placeholders left in the translated strings using authToken values.
  // This handles cases where Spanish asset entries themselves contain {{patient_FirstName}} etc.
  if (patientValues && Object.keys(patientValues).length > 0) {
    const resolve = (node) => {
      if (typeof node === 'string' && node.includes('{{')) {
        return node.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
          const k = key.trim();
          const val = patientValues[k];
          return val != null ? String(val) : match;
        });
      }
      if (Array.isArray(node)) return node.map(resolve);
      if (node && typeof node === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(node)) out[k] = resolve(v);
        return out;
      }
      return node;
    };
    wrapped.schemaData = resolve(wrapped.schemaData);
  }

  return wrapped.schemaData;
};
